import { getRecaptchaSiteKey } from "@/constants/appConstants";
import { useLogin } from "@/context/useLoginProps";
import { useRecaptchaV3 } from "@/hooks";
import { getClientId } from "@/utils";
import { Button, Edit, OTPInput } from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";

import Heading from "../../common/heading";
import { useLoginUser, useSendOTP } from "../query";
import { LoginData } from "../type";

const VerifyOTP: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const [otp, setOtp] = useState<string>("");
  const {
    hostname,
    loginStep,
    loginData,
    showToast,
    setLoginStep,
    setLoginData,
    translation: { login },
    handleEventTracking,
  } = useLogin();

  const [otpId, setOtpId] = useState<string | undefined>(loginData.otpId);
  const [otpError, setOtpError] = useState<string>("");

  const selectedEmailUuid = loginData.selectedEmailFromPopup?.uuid;

  const { executeRecaptcha } = useRecaptchaV3(
    getRecaptchaSiteKey(hostname),
    true,
  );

  const {
    mutate: loginUser,
    data: userLoginData,
    status: loginStatus,
  } = useLoginUser();

  const { mutate: sendOTP, data: sendOtpData } = useSendOTP();

  const loginLoading = loginStatus === "pending";

  const isEmail = loginStep === "VERIFY_EMAIL";

  useEffect(() => {
    if (userLoginData?.redirectUrl) {
      window.location.href = userLoginData?.redirectUrl;
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_OTP_Verified",
        eventProps: {
          status: "success",
        },
      });

      isEmail &&
        handleEventTracking({
          eventType: "onUserLoginPush",
          eventProps: {
            email: loginData.username,
          },
        });
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_Completed",
        eventProps: {
          method: "OTP",
        },
      });
    } else if (userLoginData?.error?.message?.errorMessage) {
      showToast({
        show: true,
        type: "error",
        message: userLoginData?.error?.message?.errorMessage,
      });
    }
  }, [userLoginData]);

  const handleOTPResend = async () => {
    // Track Resend OTP with GA dataLayer
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "resend_otp",
        eventCategory: "Enter-OTP",
        eventAction: "Resend-OTP",
        eventLabel: "",
      });
    }
    handleEventTracking({
      eventType: "eventPush",
      eventName: "Login_OTP_Resend_Clicked",
      eventProps: {
        otp_channel: isEmail ? "email" : "sms",
      },
    });
    setOtpError("");
    setOtp("");
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("resend");
    const username = `${isEmail && !selectedEmailUuid ? "" : loginData.countryCode}${loginData.username}`;
    sendOTP(
      {
        username,
        reCaptchaToken,
        reCaptchaAction,
        ...(selectedEmailUuid && { uuid: selectedEmailUuid }),
      },
      {
        onError: (error) => {
          showToast({
            show: true,
            type: "error",
            message: error.message,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (sendOtpData && sendOtpData.id) {
      setLoginData((prevData: LoginData) => {
        const updated = { ...prevData, otpId: sendOtpData.id };
        return updated;
      });
      setOtpId(sendOtpData.id);
    }
  }, [sendOtpData, setLoginData]);

  useEffect(() => {
    setOtpId(loginData.otpId);
  }, [loginData.otpId]);

  const editHandler = () => {
    setLoginStep("LOGIN");
    setLoginData((prevData: LoginData) => {
      const updated = {
        username: prevData.username,
        countryCode: prevData.countryCode,
      };
      return updated;
    });
  };

  const handleOTPSubmit = () => {
    if (otp.length === 6) {
      setOtpError("");
      const client_id = params.get("client_id");
      const username = `${isEmail && !selectedEmailUuid ? "" : loginData.countryCode}${loginData.username}`;
      const queryParams = new URLSearchParams(window.location.search);
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_OTP_Entered",
        eventProps: {
          otp_entered_value: otp,
        },
      });

      loginUser(
        {
          username,
          otp,
          otp_id: otpId ?? "",
          client_id: getClientId(client_id || undefined),
          ...(selectedEmailUuid && { uuid: selectedEmailUuid }),
          queryParams,
        },
        {
          onError: (error) => {
            setOtpError(
              error instanceof Error
                ? error.message
                : "Login failed. Please try again.",
            );
            handleEventTracking({
              eventType: "eventPush",
              eventName: "Login_OTP_Verification_Error",
              eventProps: {
                error_type:
                  error instanceof Error ? error.message : "wrong_otp",
              },
            });
          },
        },
      );
    }
  };

  return (
    <>
      <Heading
        title={isEmail ? login.verifyEmail : login.verifyPhone}
        text={isEmail ? login.sentOtpOnEmail : login.sentOtpOnNumber}
      />
      <div className="text-content-tertiary flex flex-wrap items-center gap-1 text-sm font-semibold">
        {selectedEmailUuid
          ? loginData.selectedEmailFromPopup?.maskedEmail
          : `${isEmail ? "" : loginData?.countryCode} ${loginData?.username}`}
        <Edit
          width={16}
          height={16}
          color="#6E00BE"
          onClick={editHandler}
          className="cursor-pointer"
        />
      </div>

      <div className="my-[52px]">
        <OTPInput
          otpValue={otp}
          resendTime={30}
          errorMessage={otpError}
          otpUpdateHandler={(newOTP: string) => {
            setOtp(newOTP);
            setOtpError("");
          }}
          resendClickHandler={handleOTPResend}
        />
      </div>

      <Button
        size="lg"
        id="loginSubmit"
        modifier="primary"
        aria-label="submit"
        className="w-full rounded-full"
        disabled={otp.length === 6 ? false : true}
        label={login.loginText}
        onClick={() => handleOTPSubmit()}
        isLoading={loginLoading}
      />

      <div className="mt-5 text-center">
        <span
          className="text-brand-primary cursor-pointer text-sm font-medium"
          onClick={() => setLoginStep("LOGIN")}
        >
          {login.loginViaPassword}
        </span>
      </div>
    </>
  );
};

export default VerifyOTP;
